"use client";

import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";
import {
  type OpticsKind,
  type OpticsSolution,
  rayModel,
} from "@/lib/tool-kit/physics-waves/optics";

export interface OpticsFigureStrings {
  figureLabel: string;
  figureDescription: string;
  objectLabel: string;
  imageLabel: string;
  imageTooFar: string;
}

const WIDTH = 600;
const HEIGHT = 300;
const CX = WIDTH / 2;
const AXIS = HEIGHT / 2;
const HALF_WIDTH = 270;
const HALF_HEIGHT = 74;
const RAY_COLORS = {
  1: CHART_COLORS[0],
  2: CHART_COLORS[1],
  3: CHART_COLORS[2],
} as const;
/** Hatch marks on the back of the mirror. */
const HATCH = Array.from({ length: 12 }, (_, i) => AXIS - 105 + i * 18);
/** An image further than this many times the object's distance is not drawn. */
const FAR_LIMIT = 6;

function Arrow({
  x,
  tip,
  color,
  dashed,
}: {
  x: number;
  tip: number;
  color: string;
  dashed: boolean;
}) {
  const direction = tip < AXIS ? -1 : 1;
  const head = Math.min(9, Math.abs(tip - AXIS) / 2);
  return (
    <g>
      <line
        stroke={color}
        strokeDasharray={dashed ? "5 4" : undefined}
        strokeWidth={3}
        x1={px(x)}
        x2={px(x)}
        y1={AXIS}
        y2={px(tip)}
      />
      <polygon
        fill={color}
        points={`${px(x)},${px(tip)} ${px(x - 5)},${px(tip - direction * head)} ${px(x + 5)},${px(tip - direction * head)}`}
      />
    </g>
  );
}

/** A ray diagram with the three principal rays, drawn to a common horizontal scale. */
export function OpticsFigure({
  kind,
  solution,
  objectHeight,
  strings,
}: {
  kind: OpticsKind;
  solution: OpticsSolution;
  objectHeight: number;
  strings: OpticsFigureStrings;
}) {
  const far =
    Math.abs(solution.dImage) >
    FAR_LIMIT * Math.max(solution.dObject, Math.abs(solution.f));
  const model = rayModel(kind, solution, objectHeight, 1);
  const xs = [
    -solution.dObject,
    0,
    ...model.focalPoints,
    ...(far ? [] : [model.image.x]),
  ];
  const half = Math.max(...xs.map(Math.abs)) * 1.12;
  const sx = HALF_WIDTH / half;
  const tallest = Math.max(
    Math.abs(objectHeight),
    far ? 0 : Math.abs(model.image.y),
    1e-9
  );
  const sy = HALF_HEIGHT / tallest;
  const full = rayModel(kind, solution, objectHeight, half);
  const X = (x: number) => CX + x * sx;
  const Y = (y: number) => AXIS - y * sy;
  const converging = solution.f > 0;
  const virtual = solution.dImage < 0;
  return (
    <ChartSvg
      className="mx-auto block h-auto w-full max-w-2xl"
      description={strings.figureDescription}
      height={HEIGHT}
      id="tl-figure"
      label={strings.figureLabel}
      width={WIDTH}
    >
      <defs>
        <clipPath id="tl-clip">
          <rect height={HEIGHT} width={WIDTH} x={0} y={0} />
        </clipPath>
      </defs>
      <line
        stroke={CHART_INK.axis}
        strokeWidth={1.5}
        x1={10}
        x2={WIDTH - 10}
        y1={AXIS}
        y2={AXIS}
      />
      <g clipPath="url(#tl-clip)">
        {full.segments.map((seg) => (
          <line
            key={`${seg.ray}-${seg.dashed}-${seg.x1}-${seg.y1}-${seg.x2}-${seg.y2}`}
            stroke={RAY_COLORS[seg.ray]}
            strokeDasharray={seg.dashed ? "5 4" : undefined}
            strokeWidth={1.8}
            x1={px(X(seg.x1))}
            x2={px(X(seg.x2))}
            y1={px(Y(seg.y1))}
            y2={px(Y(seg.y2))}
          />
        ))}
      </g>
      {kind === "lens" ? (
        <g>
          <line
            stroke={CHART_INK.text}
            strokeWidth={3}
            x1={CX}
            x2={CX}
            y1={AXIS - 112}
            y2={AXIS + 112}
          />
          {[-1, 1].map((side) => {
            const end = AXIS + side * 112;
            // Converging: arrowheads point out; diverging: they point in.
            const apex = converging ? end : end - side * 12;
            const base = converging ? end - side * 12 : end;
            return (
              <polygon
                fill={CHART_INK.text}
                key={side}
                points={`${CX},${apex} ${CX - 8},${base} ${CX + 8},${base}`}
              />
            );
          })}
        </g>
      ) : (
        <g>
          <line
            stroke={CHART_INK.text}
            strokeWidth={4}
            x1={CX}
            x2={CX}
            y1={AXIS - 112}
            y2={AXIS + 112}
          />
          {HATCH.map((y) => (
            <line
              key={y}
              stroke={CHART_INK.muted}
              strokeWidth={1.5}
              x1={CX}
              x2={CX + 9}
              y1={y}
              y2={y + 9}
            />
          ))}
        </g>
      )}
      {model.focalPoints.map((point) => (
        <g key={point}>
          <circle cx={px(X(point))} cy={AXIS} fill={CHART_INK.text} r={3.5} />
          <text
            fill={CHART_INK.muted}
            fontSize={12}
            textAnchor="middle"
            x={px(X(point))}
            y={AXIS + 18}
          >
            F
          </text>
        </g>
      ))}
      <Arrow
        color={CHART_INK.text}
        dashed={false}
        tip={px(Y(objectHeight))}
        x={X(-solution.dObject)}
      />
      <text
        fill={CHART_INK.text}
        fontSize={12}
        textAnchor="middle"
        x={px(X(-solution.dObject))}
        y={AXIS + 34}
      >
        {strings.objectLabel}
      </text>
      {far ? (
        <text
          fill={CHART_COLORS[3]}
          fontSize={12}
          textAnchor="end"
          x={WIDTH - 12}
          y={20}
        >
          {strings.imageTooFar}
        </text>
      ) : (
        <>
          <Arrow
            color={CHART_COLORS[3]}
            dashed={virtual}
            tip={px(Y(model.image.y))}
            x={X(model.image.x)}
          />
          <text
            fill={CHART_COLORS[3]}
            fontSize={12}
            textAnchor="middle"
            x={px(X(model.image.x))}
            y={AXIS + 34}
          >
            {strings.imageLabel}
          </text>
        </>
      )}
    </ChartSvg>
  );
}
